import { useCallback, useMemo, useRef, useState } from "react";
import {
  getDocs,
  onSnapshot,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  Query,
} from "../firebase";
import { uniqueVals } from "@markit/common.utils";
import { AccountData, Conversation, Follower } from "@markit/common.types";
import {
  getUserConversationSnap,
  getUserConversationsRef,
} from "../utils/FirebaseUtils";
import { fetchSingleFollower, fetchSingleUser } from "../utils/FetchSingleData";
import { loadAlgoliaUsersSearchResults } from "../utils/algoliaUtils";
import { useDispatch, useSelector, batch } from "react-redux";
import { accountActions, getAccountState } from "../redux/slices/accountSlice";
import useOnUnmount from "./useOnUnmount";
import {
  conversationActions,
  isUnreadConversation,
} from "../redux/slices/conversationSlice";

export enum ConversationStatus {
  ALL = "all",
  UNREAD = "unread",
  ARCHIVED = "archived",
}

type useLoadUserConversationsListProps = {
  userId: string;
  windowSize: number;
  status: ConversationStatus;
};
export const useLoadUserConversationsList = (
  props: useLoadUserConversationsListProps
) => {
  const { userId, windowSize, status } = props;
  const { followingAccountData, followersData } =
    useSelector(getAccountState).account;
  const dispatch = useDispatch();

  const [fetchedConversations, setFetchedConversations] = useState<
    Conversation[]
  >([]);
  const [fetchedUserData, setFetchedUserData] = useState<AccountData[]>([]);
  const [fetchedFollowerData, setFetchedFollowerData] = useState<Follower[]>(
    []
  );
  const unsubscribeArrayRef = useRef<(() => void)[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [timeoutEvent, setTimeoutEvent] = useState<
    NodeJS.Timeout | undefined
  >();

  const [lastVisible, setLastVisible] = useState<string>(
    new Date().toISOString()
  );
  const [loadedAllConversations, setLoadedAllConversations] = useState(false);

  useOnUnmount(
    (latestDeps) => {
      batch(() => {
        dispatch(
          accountActions.addMultipleFollowingAccountData(
            latestDeps[0] as AccountData[]
          )
        );
        dispatch(
          conversationActions.addMultipleConversations(
            latestDeps[1] as Conversation[]
          )
        );
        dispatch(
          accountActions.addMultipleFollowerData(latestDeps[2] as Follower[])
        );
      });
      unsubscribeArrayRef.current.forEach(
        (unsubscribe) => unsubscribe && unsubscribe()
      );
      unsubscribeArrayRef.current = []; // Clear the array after cleanup
    },
    [fetchedUserData, fetchedConversations, fetchedFollowerData]
  );

  const addUserData = useCallback(
    (newUserData: AccountData[], newFollowerData: Follower[]) => {
      setFetchedUserData((fetchedUserData) =>
        uniqueVals(
          fetchedUserData.concat(newUserData),
          (userData) => userData.uid
        )
      );
      setFetchedFollowerData((fetchedFollowerData) =>
        uniqueVals(
          fetchedFollowerData.concat(newFollowerData),
          (followerData) => followerData.uid
        )
      );
    },
    []
  );

  const removeUserData = useCallback((newUserDataUid: string) => {
    setFetchedUserData((fetchedUserData) => {
      const index = fetchedUserData.findIndex(
        (fetched) => fetched.uid === newUserDataUid
      );
      if (index !== -1) {
        fetchedUserData.splice(index, 1);
      }
      return [...fetchedUserData];
    });
    setFetchedFollowerData((fetchedFollowerData) => {
      const index = fetchedFollowerData.findIndex(
        (fetched) => fetched.uid === newUserDataUid
      );
      if (index !== -1) {
        fetchedFollowerData.splice(index, 1);
      }
      return [...fetchedFollowerData];
    });
  }, []);

  const addConversationData = useCallback((conversations: Conversation[]) => {
    setFetchedConversations((fetchedConversations) =>
      uniqueVals(
        conversations.concat(fetchedConversations),
        (conversation) => conversation.uid
      )
    );
  }, []);

  const removeConversationData = useCallback((participantUid: string) => {
    setFetchedConversations((fetchedConversations) => {
      const index = fetchedConversations.findIndex(
        (fetched) => fetched.uid === participantUid
      );
      if (index !== -1) {
        fetchedConversations.splice(index, 1);
      }
      return uniqueVals(
        fetchedConversations,
        (conversation) => conversation.uid
      );
    });
  }, []);

  const updateConversationData = useCallback(
    (conversation: Conversation) => {
      setFetchedConversations((fetchedConversations) => {
        return fetchedConversations.map((conv) => {
          if (conv.uid === conversation.uid) {
            return {
              ...conv,
              chatIndicator: conv.chatIndicator.map((m) =>
                m.uid === userId ? { ...m, indicator: false } : m
              ),
            };
          }
          return conv;
        });
      });
    },
    [userId]
  );

  const isFinished = useMemo(
    () => loadedAllConversations,
    [loadedAllConversations]
  );

  const addConversationAndUser = useCallback(
    async (conversation: Conversation) => {
      addConversationData([conversation]);
      const [userData, followerData] = await Promise.all([
        fetchSingleUser(conversation.uid, followingAccountData),
        fetchSingleFollower(userId, conversation.uid, followersData),
      ]);
      if (userData && followerData) {
        addUserData([userData], [followerData]);
      }
    },
    [
      addConversationData,
      addUserData,
      followersData,
      followingAccountData,
      userId,
    ]
  );

  const retrieveConversationResults = useCallback(
    async (
      conversationsQuery: Query<Conversation>
    ): Promise<Conversation[]> => {
      const conversationsSnap = await getDocs(conversationsQuery);
      const uploadedTimes = conversationsSnap.docs.map(
        (doc) => doc.data().recentTimestamp
      );
      if (uploadedTimes.length > 0) {
        setLastVisible(uploadedTimes[uploadedTimes.length - 1]);
      }
      if (uploadedTimes.length < windowSize) {
        setLoadedAllConversations(true);
      }

      const conversations = await Promise.all(
        conversationsSnap.docs
          .map((doc) => doc.data())
          .map(async (conversation) => {
            await addConversationAndUser(conversation);
            return conversation;
          })
      );
      addConversationData(conversations);
      setIsLoading(false);
      return conversations;
    },
    [addConversationAndUser, addConversationData, windowSize]
  );

  const fetchConversations = useCallback(async () => {
    if (!isFinished && !isLoading) {
      setIsLoading(true);
      const userConversationsRef = getUserConversationsRef(userId);
      const baseQuery = [
        where("recentTimestamp", ">", ""),
        orderBy("recentTimestamp", "desc"),
        limit(windowSize),
      ];

      const statusQuery =
        status === ConversationStatus.UNREAD
          ? [
              where("chatIndicator", "array-contains", {
                indicator: true,
                uid: userId,
              }),
            ]
          : [where("archived", "==", status === ConversationStatus.ARCHIVED)];

      const userConversationsQuery = query(
        userConversationsRef,
        ...baseQuery,
        ...statusQuery
      );
      await retrieveConversationResults(userConversationsQuery);

      const unsubscribe = onSnapshot(userConversationsQuery, (snapshot) => {
        snapshot.docChanges().forEach(async (message) => {
          const conversation = message.doc.data();
          if (message.type === "added") {
            addConversationAndUser(conversation);
          } else if (message.type === "modified") {
            addConversationAndUser(conversation);
          } else if (message.type === "removed") {
            const isUnread = isUnreadConversation(
              conversation.chatIndicator,
              userId
            );
            // We only want to update the unread conversation instead of removing it while viewing the unread tab
            if (status === ConversationStatus.UNREAD && isUnread) {
              updateConversationData(conversation);
            } else {
              removeConversationData(conversation.uid);
              removeUserData(conversation.uid);
            }
          }
        });
      });

      unsubscribeArrayRef.current.push(unsubscribe);
    }
  }, [
    isFinished,
    isLoading,
    userId,
    status,
    windowSize,
    retrieveConversationResults,
    addConversationAndUser,
    updateConversationData,
    removeConversationData,
    removeUserData,
  ]);

  const fetchMoreConversations = useCallback(async () => {
    if (!isFinished && !isLoading) {
      setIsLoading(true);
      const userConversationsRef = getUserConversationsRef(userId);
      const baseQuery = [
        where("recentTimestamp", ">", ""),
        orderBy("recentTimestamp", "desc"),
        startAfter(lastVisible),
        limit(windowSize),
      ];

      const statusQuery =
        status === ConversationStatus.UNREAD
          ? [
              where("chatIndicator", "array-contains", {
                indicator: true,
                uid: userId,
              }),
            ]
          : [where("archived", "==", status === ConversationStatus.ARCHIVED)];

      const userConversationsQuery = query(
        userConversationsRef,
        ...baseQuery,
        ...statusQuery
      );
      await retrieveConversationResults(userConversationsQuery);

      const unsubscribe = onSnapshot(userConversationsQuery, (snapshot) => {
        snapshot.docChanges().forEach(async (message) => {
          const conversation = message.doc.data();
          if (message.type === "added") {
            addConversationAndUser(conversation);
          } else if (message.type === "modified") {
            addConversationAndUser(conversation);
          } else if (message.type === "removed") {
            const isUnread = isUnreadConversation(
              conversation.chatIndicator,
              userId
            );
            if (status === ConversationStatus.UNREAD && isUnread) {
              updateConversationData(conversation);
            } else {
              removeConversationData(conversation.uid);
              removeUserData(conversation.uid);
            }
          }
        });
      });

      unsubscribeArrayRef.current.push(unsubscribe);
    }
  }, [
    isFinished,
    isLoading,
    userId,
    status,
    lastVisible,
    windowSize,
    retrieveConversationResults,
    addConversationAndUser,
    updateConversationData,
    removeConversationData,
    removeUserData,
  ]);

  const loadSearchResults = useCallback(
    async (searchTerm: string) => {
      const fetchConversations = async (userIds: string[]) => {
        setIsLoading(true);
        await Promise.all(
          userIds.map(async (uid) => {
            // Conversation docs are keyed by the participant's uid
            const conversation = (
              await getUserConversationSnap(userId, uid)
            ).data();
            const matchesStatus =
              conversation &&
              (status === ConversationStatus.UNREAD
                ? isUnreadConversation(conversation.chatIndicator, userId)
                : conversation.archived ===
                  (status === ConversationStatus.ARCHIVED));
            if (conversation && conversation.recentTimestamp && matchesStatus) {
              addConversationAndUser(conversation);
            }
          })
        );
        setIsLoading(false);
      };
      await loadAlgoliaUsersSearchResults(
        searchTerm,
        `following:${userId}`,
        (userIds: string[]) => fetchConversations(userIds)
      );
    },
    [userId, status, addConversationAndUser]
  );

  const loadSearchResultsThrottled = useCallback(
    (searchTerm: string) => {
      if (timeoutEvent) {
        clearTimeout(timeoutEvent);
      }
      setTimeoutEvent(
        setTimeout(() => {
          if (searchTerm.length > 1) {
            loadSearchResults(searchTerm);
          }
        }, 500)
      );
    },
    [loadSearchResults, timeoutEvent]
  );

  const resetLoadedUsers = useCallback(() => {
    setLoadedAllConversations(false);
    setFetchedUserData([]);
    setFetchedConversations([]);
    setLastVisible(new Date().toISOString());
    setIsLoading(false);
  }, []);

  return {
    isFinished,
    isLoading,
    fetchedUserData,
    fetchedFollowerData,
    fetchedConversations,
    fetchConversations,
    fetchMoreConversations,
    loadSearchResultsThrottled,
    resetLoadedUsers,
  };
};
